import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Loader2, MapPin, Users, Star, Calendar as CalendarIcon, 
  Wifi, Car, Coffee, Home, ChevronLeft, Check, Clock, 
  Bed, Bath, Shield, Building2, Zap, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInDays, addDays } from 'date-fns';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Amenity icons mapping
const AMENITY_ICONS = {
  wifi: { icon: Wifi, label: 'Wi-Fi' },
  parking: { icon: Car, label: 'Parking' },
  kitchen: { icon: Coffee, label: 'Kitchen' },
  workspace: { icon: Home, label: 'Workspace' },
  heating: { icon: Home, label: 'Heating' },
  air_conditioning: { icon: Home, label: 'A/C' },
  tv: { icon: Home, label: 'TV' },
  laundry: { icon: Home, label: 'Laundry' },
  breakfast: { icon: Coffee, label: 'Breakfast' },
  elevator: { icon: Building2, label: 'Elevator' },
  security: { icon: Shield, label: 'Security' },
};

/**
 * Photo Gallery Component
 */
const PhotoGallery = ({ photos, title }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const displayPhotos = photos?.length > 0 ? photos : [
    'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'
  ];
  
  return (
    <div className="space-y-2">
      <div className="relative aspect-[16/9] rounded-lg overflow-hidden">
        <img 
          src={displayPhotos[selectedPhoto]}
          alt={title}
          className="w-full h-full object-cover"
        />
      </div>
      {displayPhotos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {displayPhotos.slice(0, 5).map((photo, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedPhoto(idx)}
              className={cn(
                "flex-shrink-0 w-20 h-16 rounded overflow-hidden border-2 transition-all",
                selectedPhoto === idx ? "border-purple-500" : "border-transparent"
              )}
            >
              <img src={photo} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Booking Widget Component
 */
const BookingWidget = ({ listing, t }) => {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('1');
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  
  // Booking form state
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [message, setMessage] = useState('');
  const [purpose, setPurpose] = useState('work');
  const [submitting, setSubmitting] = useState(false);

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;

  const checkAvailability = async () => {
    if (!checkIn || !checkOut) return;
    
    setCheckingAvailability(true);
    try {
      const response = await fetch(`${API_URL}/api/accommodation/availability/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: listing.listing_id,
          check_in: format(checkIn, 'yyyy-MM-dd'),
          check_out: format(checkOut, 'yyyy-MM-dd'),
          guests: parseInt(guests)
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Failed to check availability');
        setAvailability(null);
        return;
      }
      
      setAvailability(data);
    } catch (error) {
      console.error('Error checking availability:', error);
      toast.error('Failed to check availability');
    } finally {
      setCheckingAvailability(false);
    }
  };

  useEffect(() => {
    if (checkIn && checkOut && nights > 0) {
      checkAvailability();
    }
  }, [checkIn, checkOut, guests]);

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!availability?.available) {
      toast.error('Please check availability first');
      return;
    }
    
    if (!guestName || !guestEmail) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/accommodation/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: listing.listing_id,
          check_in: format(checkIn, 'yyyy-MM-dd'),
          check_out: format(checkOut, 'yyyy-MM-dd'),
          guests: parseInt(guests),
          guest_name: guestName,
          guest_email: guestEmail,
          guest_phone: guestPhone || null,
          message: message || null,
          purpose: purpose
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Booking failed');
        return;
      }
      
      toast.success(data.booking_mode === 'instant' 
        ? t('accommodation.booking.successInstant') 
        : t('accommodation.booking.successRequest')
      );
      
      // Navigate to booking confirmation
      navigate(`/accommodation/booking/${data.booking_id}?email=${encodeURIComponent(guestEmail)}`);
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="sticky top-4">
      <CardContent className="p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <span className="text-2xl font-bold">€{listing.nightly_price}</span>
            <span className="text-gray-500"> / {t('accommodation.booking.night')}</span>
          </div>
          {listing.rating && (
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="font-medium">{listing.rating}</span>
            </div>
          )}
        </div>

        {/* Date Selection */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div>
            <Label className="text-xs text-gray-500">{t('accommodation.browse.checkIn')}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {checkIn ? format(checkIn, 'MMM d') : 'Select'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={checkIn}
                  onSelect={(date) => {
                    setCheckIn(date);
                    if (date && (!checkOut || checkOut <= date)) {
                      setCheckOut(addDays(date, listing.min_nights || 1));
                    }
                  }}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-xs text-gray-500">{t('accommodation.browse.checkOut')}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {checkOut ? format(checkOut, 'MMM d') : 'Select'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={checkOut}
                  onSelect={setCheckOut}
                  disabled={(date) => date <= (checkIn || new Date())}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Guests */}
        <div className="mb-4">
          <Label className="text-xs text-gray-500">{t('accommodation.browse.guests')}</Label>
          <Select value={guests} onValueChange={setGuests}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...Array(listing.max_occupants || 6)].map((_, i) => (
                <SelectItem key={i+1} value={String(i+1)}>
                  {i+1} {t('accommodation.browse.guest')}{i > 0 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Availability/Price Display */}
        {checkingAvailability ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
          </div>
        ) : availability ? (
          availability.available ? (
            <div className="border rounded-lg p-4 mb-4 bg-green-50">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <Check className="h-4 w-4" />
                <span className="font-medium">{t('accommodation.booking.available')}</span>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>€{listing.nightly_price} × {nights} {t('accommodation.booking.nights')}</span>
                  <span>€{availability.pricing?.subtotal}</span>
                </div>
                {availability.pricing?.discount_type !== 'nightly' && (
                  <div className="flex justify-between text-green-600">
                    <span>{availability.pricing?.discount_type} discount</span>
                    <span>Applied</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>{t('accommodation.booking.serviceFee')}</span>
                  <span>€{availability.pricing?.service_fee}</span>
                </div>
                <div className="flex justify-between font-bold pt-2 border-t">
                  <span>{t('accommodation.booking.total')}</span>
                  <span>€{availability.pricing?.total}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="border rounded-lg p-4 mb-4 bg-red-50">
              <p className="text-red-700 text-sm">{t('accommodation.booking.notAvailable')}</p>
            </div>
          )
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">
            {t('accommodation.booking.selectDates')}
          </p>
        )}

        {/* Booking Form */}
        {availability?.available && !showBookingForm && (
          <Button 
            className="w-full bg-purple-600 hover:bg-purple-700"
            onClick={() => setShowBookingForm(true)}
          >
            {listing.booking_mode === 'instant' 
              ? t('accommodation.booking.bookNow') 
              : t('accommodation.booking.sendRequest')
            }
          </Button>
        )}

        {showBookingForm && (
          <form onSubmit={handleBooking} className="space-y-4 mt-4 pt-4 border-t">
            <h4 className="font-medium">{t('accommodation.booking.guestInfo')}</h4>
            
            <div>
              <Label>{t('accommodation.booking.name')} *</Label>
              <Input 
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                required
                data-testid="booking-guest-name"
              />
            </div>
            
            <div>
              <Label>{t('accommodation.booking.email')} *</Label>
              <Input 
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                required
                data-testid="booking-guest-email"
              />
            </div>
            
            <div>
              <Label>{t('accommodation.booking.phone')}</Label>
              <Input 
                type="tel"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                data-testid="booking-guest-phone"
              />
            </div>
            
            <div>
              <Label>{t('accommodation.booking.purpose')}</Label>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="work">{t('accommodation.booking.purposeWork')}</SelectItem>
                  <SelectItem value="vacation">{t('accommodation.booking.purposeVacation')}</SelectItem>
                  <SelectItem value="other">{t('accommodation.booking.purposeOther')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('accommodation.booking.message')}</Label>
              <Textarea 
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('accommodation.booking.messagePlaceholder')}
                rows={3}
                data-testid="booking-message"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={submitting}
              data-testid="submit-booking"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
              ) : listing.booking_mode === 'instant' ? (
                <><Zap className="h-4 w-4 mr-2" /> {t('accommodation.booking.bookNow')}</>
              ) : (
                t('accommodation.booking.sendRequest')
              )}
            </Button>
            
            {listing.booking_mode === 'instant' && (
              <p className="text-xs text-center text-green-600 flex items-center justify-center gap-1">
                <Zap className="h-3 w-3" />
                {t('accommodation.booking.instantConfirm')}
              </p>
            )}
          </form>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Main Listing Detail Page
 */
const AccommodationListingPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchListing();
  }, [listingId]);

  const fetchListing = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/accommodation/listings/${listingId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Listing not found');
        } else {
          setError('Failed to load listing');
        }
        return;
      }
      
      const data = await response.json();
      setListing(data);
    } catch (err) {
      console.error('Error fetching listing:', err);
      setError('Failed to load listing');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <Building2 className="h-16 w-16 text-gray-300" />
        <h2 className="text-xl font-semibold text-gray-700">{error || t('accommodation.errors.notFound')}</h2>
        <Button onClick={() => navigate('/accommodation')}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/accommodation')}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('common.back')}
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photos */}
            <PhotoGallery photos={listing.photos} title={listing.title} />

            {/* Title & Basic Info */}
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{listing.title}</h1>
                  <p className="text-gray-500 flex items-center gap-1 mt-1">
                    <MapPin className="h-4 w-4" />
                    {listing.city}, {listing.country}
                  </p>
                </div>
                <div className="flex gap-2">
                  {listing.work_friendly && (
                    <Badge className="bg-blue-100 text-blue-700">
                      {t('accommodation.browse.workFriendly')}
                    </Badge>
                  )}
                  {listing.booking_mode === 'instant' && (
                    <Badge className="bg-green-100 text-green-700">
                      <Zap className="h-3 w-3 mr-1" />
                      {t('accommodation.browse.instantBook')}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {listing.max_occupants} {t('accommodation.listing.maxGuests')}
                </span>
                {listing.bedrooms && (
                  <span className="flex items-center gap-1">
                    <Bed className="h-4 w-4" />
                    {listing.bedrooms} {t('accommodation.listing.bedrooms')}
                  </span>
                )}
                {listing.bathrooms && (
                  <span className="flex items-center gap-1">
                    <Bath className="h-4 w-4" />
                    {listing.bathrooms} {t('accommodation.listing.bathrooms')}
                  </span>
                )}
                <span className="capitalize bg-gray-100 px-2 py-1 rounded">
                  {t(`accommodation.types.${listing.accommodation_type}`) || listing.accommodation_type?.replace('_', ' ')}
                </span>
              </div>
            </div>

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>{t('accommodation.listing.about')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 whitespace-pre-wrap">{listing.description}</p>
              </CardContent>
            </Card>

            {/* Amenities */}
            {listing.amenities?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('accommodation.listing.amenities')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {listing.amenities.map((amenity) => {
                      const amenityData = AMENITY_ICONS[amenity] || { icon: Home, label: amenity };
                      const Icon = amenityData.icon;
                      return (
                        <div key={amenity} className="flex items-center gap-2 text-gray-600">
                          <Icon className="h-5 w-5 text-purple-500" />
                          <span className="capitalize">{amenityData.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* House Rules */}
            {listing.house_rules && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('accommodation.listing.houseRules')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 whitespace-pre-wrap">{listing.house_rules}</p>
                  <div className="flex gap-6 mt-4 text-sm">
                    <div>
                      <p className="text-gray-500">{t('accommodation.listing.checkInTime')}</p>
                      <p className="font-medium flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {listing.check_in_time || '15:00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">{t('accommodation.listing.checkOutTime')}</p>
                      <p className="font-medium flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {listing.check_out_time || '11:00'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Host Info */}
            <Card>
              <CardHeader>
                <CardTitle>{t('accommodation.listing.host')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-2xl font-bold text-purple-600">
                      {listing.host_name?.[0]?.toUpperCase() || 'H'}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-semibold">{listing.host_name || 'Host'}</h4>
                    {listing.is_verified_host && (
                      <Badge variant="outline" className="mt-1">
                        <Shield className="h-3 w-3 mr-1" />
                        {t('accommodation.listing.verifiedHost')}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking Widget - Sidebar */}
          <div className="lg:col-span-1">
            <BookingWidget listing={listing} t={t} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccommodationListingPage;
